// @ts-nocheck

import { pipe, set } from '@duda-co/fp';
import { get, getFlag } from '../common-props';
import { LogLevel } from './index';
const noop = () => {};
const setFieldOrEmpty = (fieldName, value) => value ? {
  [fieldName]: value
} : {};
const JSON_MIME_TYPE = 'application/json';
const WINDOW_VERSION_FIELD = 'version';
const LOG_SINK_URL = '/_dm/s/rt/actions/logs';
const isSentryReportFromLoggerEnabled = getFlag('sentry.report.on.client.logs.enabled');
const formatAsSimpleTime = function (date) {
  return ['getHours', 'getMinutes', 'getSeconds'].map(method => new Date(date)[method]().toString().padStart(2, '0')).join(':');
};
const getFriendlySummary = data => typeof data === 'string' ? data : ['message', 'msg', 'text', 'error', 'desc', 'description', 'dataString'].reduce((prev, cur) => {
  const curMessage = data[cur];
  return prev || (typeof curMessage === 'string' ? curMessage : null);
}, null);
export const pipeFormatMessage = next => _ref => {
  let {
    logLevel: level,
    dataString: data
  } = _ref;
  return next({
    data,
    level: level
  });
};
export const pipeAddContext = next => data => next(Object.assign(data, {
  userAgent: window?.navigator?.userAgent,
  url: window?.location?.href,
  timestamp: Date.now()
}));
export const pipeFilterByLogLevel = thresholdLevel => {
  const getLevelPriority = level => [LogLevel.TRACE, LogLevel.DEBUG, LogLevel.INFO, LogLevel.WARN, LogLevel.ERROR].indexOf(level);
  return next => _ref2 => {
    let {
      level,
      ...data
    } = _ref2;
    return getLevelPriority(level) >= getLevelPriority(thresholdLevel) && next({
      level,
      ...data
    });
  };
};
export const pipeFilterSkipJournal = next => _ref3 => {
  let {
    data,
    data: {
      _skipJournal = false
    } = {},
    ...event
  } = _ref3;
  (_skipJournal ? noop : next)({
    data,
    ...event
  });
};
export const pipeFormatBatchForSink = function () {
  const FORMATTERS = [{
    test: obj => obj instanceof Error,
    format: obj => ['message', 'stack', 'code', 'cause'].reduce((fObj, fieldName) => ({
      ...setFieldOrEmpty(fieldName, obj[fieldName]),
      ...fObj
    }), {})
  }, {
    test: obj => ['string', 'number'].includes(typeof obj),
    format: obj => ({
      message: obj
    })
  }, {
    test: () => true,
    format: obj => Object.entries(obj).reduce((fObj, _ref4) => {
      let [key, value] = _ref4;
      return {
        [key]: ['number', 'string'].includes(typeof value) ? value : JSON.stringify(value),
        ...fObj
      };
    }, {})
  }];
  return next => messageBuffer => {
    next({
      logs: messageBuffer.map(_ref5 => {
        let {
          data,
          level,
          timestamp,
          userAgent,
          url,
          version,
          environment
        } = _ref5;
        const friendlySummary = getFriendlySummary(data);
        return {
          priority: level.toUpperCase(),
          ...setFieldOrEmpty('log', friendlySummary),
          fields: {
            _ts: timestamp,
            _url: url,
            _userAgent: userAgent,
            ...setFieldOrEmpty('_ver', version),
            ...setFieldOrEmpty('_env', environment),
            ...FORMATTERS.find(_ref6 => {
              let {
                test
              } = _ref6;
              return test(data);
            }).format(data)
          }
        };
      })
    });
  };
}();
export const pipeAddVersionContext = function (windowVersion) {
  if (windowVersion === void 0) {
    windowVersion = '';
  }
  const [environment, version] = (windowVersion.match(/^(\w+)_([0-9]+)$/) || []).slice(1);
  return next => pipe(...[environment && set('environment', environment), version && set('version', Number(version)), next].filter(Boolean));
}((func => func(func, window))((func, val) => val && (val[WINDOW_VERSION_FIELD] || (val.parent !== val ? func(func, val.parent) : ''))));
export const pipeDispatchLogEventToConsole = () => _ref7 => {
  let {
    text,
    level
  } = _ref7;
  const logFun = console?.[level] ?? console.log;
  logFun(text);
};
export const pipeFormatAsTextLine = next => _ref8 => {
  let {
    data,
    level,
    timestamp
  } = _ref8;
  return next({
    level,
    text: `[${formatAsSimpleTime(timestamp)}] ${getFriendlySummary(data) || 'Cannot find a description for the message sent. See "fields" for more information.'}`
  });
};
export const pipeDispatchLogEventToSink = next => data => {
  navigator.sendBeacon ? navigator.sendBeacon(LOG_SINK_URL, new Blob([JSON.stringify(data)], {
    type: JSON_MIME_TYPE
  })) : globalThis?.fetch?.(LOG_SINK_URL, {
    headers: {
      'content-type': JSON_MIME_TYPE
    },
    credentials: 'omit',
    cache: 'no-store',
    method: 'POST',
    body: JSON.stringify(data)
  });
  return next(data);
};

/**
 * This pipe function sends log to Sentry if Sentry exists and only if the log
 * level is Error
 * @param next
 * @returns
 */
export const pipeDispatchSentryLogEvent = next => data => {
  if (isSentryReportFromLoggerEnabled && window?.Sentry) {
    const {
      logs
    } = data;
    if (logs.length) {
      logs.forEach(log => {
        if (log.priority === 'ERROR') {
          const {
            log: logMsg
          } = log;
          const customError = new Error(`[FROM LOGGER] - ${logMsg}`);
          window.Sentry.captureException(customError);
        }
      });
    }
  }
  return next?.(data);
};
export const pipeBlockLocalhost = next => event => (!get('server.for.resources')?.includes('localhost') ? next : noop)(event);
//# sourceMappingURL=log-event-pipeline.js.map